import React, { forwardRef, useState } from 'react';
import clsx from 'clsx';
import { Eye, EyeOff } from 'lucide-react';

const Input = forwardRef(
  (
    {
      type = 'text',
      label,
      placeholder,
      value,
      onChange,
      onBlur,
      onFocus,
      error,
      helperText,
      disabled = false,
      required = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      className,
      inputClassName,
      rows = 4,
      name,
      id,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);

    const inputId = id || name || `input-${Math.random().toString(36).substr(2, 9)}`;

    const baseInputStyles =
      'w-full px-4 py-2.5 text-sm text-neutral-800 dark:text-neutral-100 bg-white dark:bg-neutral-800 border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:bg-neutral-50 dark:disabled:bg-neutral-900 disabled:text-neutral-400 disabled:cursor-not-allowed placeholder:text-neutral-400 dark:placeholder:text-neutral-500';

    const dateInputStyles = type === 'date' ? 'cursor-pointer [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:hover:opacity-70' : '';

    const borderStyles = error
      ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
      : 'border-neutral-200 dark:border-neutral-700 focus:border-primary-400 focus:ring-primary-100 dark:focus:ring-primary-900/30';

    const iconPaddingStyles = clsx(
      leftIcon && 'pl-11',
      rightIcon && type !== 'password' && 'pr-11',
      type === 'password' && 'pr-11'
    );

    const inputClasses = clsx(baseInputStyles, borderStyles, iconPaddingStyles, dateInputStyles, inputClassName);

    const containerClasses = clsx('flex flex-col', fullWidth ? 'w-full' : 'w-auto', className);

    const inputType = type === 'password' && showPassword ? 'text' : type;

    return (
      <div className={containerClasses}>
        {label && (
          <label
            htmlFor={inputId}
            className={clsx(
              'block text-sm font-medium mb-2',
              error ? 'text-red-500' : 'text-neutral-700 dark:text-neutral-300',
              disabled && 'text-neutral-400'
            )}
          >
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400">
              {leftIcon}
            </div>
          )}

          {type === 'textarea' ? (
            <textarea
              ref={ref}
              id={inputId}
              name={name}
              value={value}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              placeholder={placeholder}
              disabled={disabled}
              required={required}
              rows={rows}
              className={inputClasses}
              {...props}
            />
          ) : (
            <input
              ref={ref}
              type={inputType}
              id={inputId}
              name={name}
              value={value}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              placeholder={placeholder}
              disabled={disabled}
              required={required}
              className={inputClasses}
              {...props}
            />
          )}

          {type === 'password' ? (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 focus:outline-none transition-colors"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} strokeWidth={1.75} /> : <Eye size={18} strokeWidth={1.75} />}
            </button>
          ) : (
            rightIcon && (
              <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-400">
                {rightIcon}
              </div>
            )
          )}
        </div>

        {(helperText || error) && (
          <p className={clsx('text-xs mt-1.5', error ? 'text-red-500' : 'text-neutral-400')}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
